import React, { useEffect, useMemo, useState } from 'react';

import useLabEnabled from '../../hooks/useLabEnabled';
import useLabOrderStore from '../../stores/labOrderStore';
import LabSummary from './LabSummary';
import { buildLabSummary } from './labSummaryProjection';

/**
 * LabSummaryCard — the doctor's PATIENT-scoped Lab summary, shown as a collapsed
 * card on top of the prescription (mirrors the collapsible `ProcedureSummary`).
 *
 * Purpose: review what's been advised / completed / expired / cancelled across the
 * patient's recent visits — separate from the prescription's "Tests" row (which is
 * strictly what's being advised on THIS visit). Self-gates: renders nothing when
 * the lab module is off or the patient has no labs.
 */
export default function LabSummaryCard({ patientId, visitId, isDark = false, embedded = false }) {
  const labEnabled = useLabEnabled();
  const byPatient = useLabOrderStore((s) => s.byPatient);
  const fetchRecentForPatient = useLabOrderStore((s) => s.fetchRecentForPatient);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (patientId) fetchRecentForPatient(patientId);
  }, [patientId, fetchRecentForPatient]);

  // Light poll while expanded so reception/tech progress (assign → complete on the
  // lab visit's stream) reflects here without a manual refresh.
  useEffect(() => {
    if (!expanded || !patientId) return undefined;
    const t = setInterval(() => fetchRecentForPatient(patientId), 20000);
    return () => clearInterval(t);
  }, [expanded, patientId, fetchRecentForPatient]);

  const slot = patientId ? byPatient[patientId] : null;
  const orders = useMemo(() => slot?.orders || [], [slot]);
  const hasMore = !!slot?.hasMore;
  const { counts } = useMemo(() => buildLabSummary(orders), [orders]);

  if (!labEnabled || !patientId || !orders.length) return null;

  // embedded: a borderless sub-row inside the Tests block (no card chrome) so it
  // reads as part of the prescription's lab section, not a separate floating card.
  const cardClass = embedded
    ? ['border-t pt-2 mt-1', isDark ? 'border-slate-700 text-slate-200' : 'border-slate-200 text-slate-800'].join(' ')
    : [
        'rounded-md border p-3',
        isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-800',
      ].join(' ');

  return (
    <div className={cardClass}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={['w-full flex items-center justify-between gap-2 text-left', isDark ? 'text-slate-100' : 'text-slate-800'].join(' ')}
      >
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
          <span aria-hidden="true">🧪</span>
          Lab summary
          <span className={['px-1.5 py-0.5 rounded-full text-[11px] font-medium', isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-200 text-slate-700'].join(' ')}>
            {counts.all}
          </span>
        </span>
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'} aria-hidden="true">{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && (
        <div className="mt-3">
          <LabSummary orders={orders} patientId={patientId} visitId={visitId} isDark={isDark} onChanged={() => fetchRecentForPatient(patientId)} />
          {hasMore && (
            <div className={['mt-2 text-xs', isDark ? 'text-slate-400' : 'text-slate-500'].join(' ')}>
              + more in this patient's <span className="font-semibold">History</span> →
            </div>
          )}
        </div>
      )}
    </div>
  );
}
